import {
  NFTEvent,
  NFTEventEligibilityResult,
  NFTEventParam,
  NFTEventTier,
} from "@models/event";
import { translate } from "@models/language";
import { Moonpass } from "@models/moonpass";
import { INFTOwnership, NFTOwnership } from "@models/nft";
import { Transaction } from "@models/payment";
import { apiClient } from "@providers/auth";
import { isEmpty } from "lodash";
import type { GetServerSideProps } from "next";

const HOST = process.env.NEXT_PUBLIC_HOST || "https://app.moongate.id";
if (!HOST) console.error("Missing NEXT_PUBLIC_HOST");

const API_HOST = process.env.NEXT_PUBLIC_API_HOST || "";
if (!API_HOST) console.error("Missing NEXT_PUBLIC_API_HOST");

const API_KEY = process.env.NEXT_PUBLIC_API_KEY || "";
if (!API_KEY) console.error("Missing NEXT_PUBLIC_API_KEY");

export type EventPageProps = {
  eventParams: NFTEventParam;
  title?: string;
  description?: string;
  image?: string;
  url?: string;
};

export const getEventServerSide: GetServerSideProps = async (context: any) => {
  try {
    const { eventId } = context.params;
    if (API_HOST && API_KEY && eventId) {
      const res = await fetch(`${API_HOST}/events/${eventId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${API_KEY}`,
          "Cache-Control": "no-cache",
        },
      });
      const result = await res.json();
      if (!isEmpty(result?.data)) {
        const { data } = result;
        const event = new NFTEvent(data as NFTEventParam);
        const eventParams: NFTEventParam = event.json;

        const eventTitle = translate(event.info).name || "Event";
        const title = `${eventTitle} | Moongate`;
        const description = eventTitle;
        const image =
          translate(event.info).hero ||
          "https://app.moongate.id/images/logo-color.svg";
        const url = event.toURL(HOST, "/e") || "https://www.moongate.id";
        // console.log(eventParams);
        return { props: { eventParams, title, description, image, url } };
      }
    }
  } catch (error) {
    // console.error(error);
  }
  return { props: { eventParams: null } };
};

export const getEventEligibility = async (
  eventId: string,
  userId: string
): Promise<NFTEventEligibilityResult | null> => {
  const result: NFTEventEligibilityResult = await apiClient.get(
    `/events/${eventId}/user/${userId}/eligibility`
  );
  if (!result || !result.nfts || !result.whitelists) return null;
  result.nfts = result.nfts.map((n) => new NFTOwnership(n as INFTOwnership));
  result.whitelists = result.whitelists.map((n) => new Transaction(n));
  return result;
};

export const checkTierEligibility = (
  tier: NFTEventTier,
  tierNFTs: NFTOwnership[] = [],
  tierWhitelists: Transaction[] = [],
  tierMoonpass: Moonpass[] = []
) => {
  // Token gated
  if (tier.nftConstraint) {
    if (tierNFTs.length == 0) return false;
    if (!tier.validateNFTConstraint(tierNFTs)) return false;
  }

  // Moonpass gated
  if (tier.moonpassConstraint) {
    if (tierMoonpass.length == 0) return false;
  }

  // Whitelisted
  if (tier.whitelistConstraint) {
    let reservedQuantity =
      tierWhitelists.length > 0 ? tierWhitelists.slice(-1)[0].items.length : 0;
    reservedQuantity = Math.min(
      reservedQuantity,
      tier.whitelistConstraint.maxPerUser || reservedQuantity
    );
    if (reservedQuantity > 0) return true;

    // if (tier.whitelistConstraint.end > 0) {
    //   const _isExpired = Date.now() > tier.whitelistConstraint.end;
    //   if (_isExpired) return false;
    // }

    if (tier.nftConstraint) {
      const eligibleNFTCount = tier.getEligibleNFTCollectionCount(tierNFTs);
      let _maxQuantity = Math.floor(
        eligibleNFTCount / (tier.whitelistConstraint.nftRequirement || 1)
      );
      if (tier.whitelistConstraint.maxPerUser > 0)
        _maxQuantity = Math.min(
          _maxQuantity,
          tier.whitelistConstraint.maxPerUser
        );
      if (_maxQuantity == 0) return false;
    }
  }

  return true;
};
